<script>
  /** @type {{
    collapse?: boolean,
    validate?: boolean,
    success?: boolean,
    disabled?: boolean,
    visuallyDisabled?: boolean,
    cover?: boolean,
    adapt?: boolean,
    muted?: boolean,
    size?: 'xs' | 'sm' | 'lg' | 'xl',
    children: Svelte.Snippet,
    [attribute: string]: any
  }} */
  let {
    collapse = false,
    validate = false,
    success = false,
    disabled = false,
    visuallyDisabled = false,
    cover = false,
    adapt = false,
    muted = false,
    size,
    children,
    ...attrs
  } = $props()

  const onclick = (event) => {
    if (validate) {
      const form = /** @type {HTMLButtonElement?} */ (event.currentTarget)?.form
      if (form && !form.reportValidity()) {
        event.preventDefault()
        return
      }
    }
    attrs.onclick?.(event)
  }
</script>

<svelte:element
  this={attrs.href ? 'a' : 'button'}
  role={attrs.href ? 'link' : 'button'}
  {...attrs}
  class="button {size ?? ''}"
  class:disabled={disabled || visuallyDisabled}
  class:validate
  class:collapse
  class:cover
  class:adapt
  class:success
  class:muted
  {onclick}
  disabled={attrs.href ? null : disabled}>
  {@render children()}
</svelte:element>

<style>
  .button {
    --transition: var(--box-shadow-transition), var(--color-transition);
    --font-size: 1.125rem;

    /* Turn "on" a switch that will allow a fallback variable to be used */
    --unless-disabled: initial;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    letter-spacing: 0;
    padding: 0.8rem 1.2rem;
    border: 1px solid transparent;
    white-space: nowrap;
    user-select: none;
    font-size: var(--current-font-size, var(--font-size));
    font-weight: 550;
    line-height: 1;
    background: rgb(var(--rgb-primary, var(--rgb-red)));
    color: rgb(var(--rgb-white));
    border-radius: var(--border-radius-full);
    box-shadow: var(--box-shadow-sm);
    transition: var(--transition);
    cursor: pointer;
  }

  .button::-webkit-details-marker {
    display: none;
  }

  .lg {
    --font-size: 1.25rem;

    padding: 0.9rem 1.25rem;
  }

  .sm {
    --font-size: 1rem;

    padding: 0.6rem 1rem;
  }

  @media (min-width: 1000px) {
    .button:not(.sm, .xs) {
      --font-size: 1.25rem;

      padding: 0.9rem 1.4rem;
    }

    .sm {
      --font-size: 1.125rem;
    }

    .lg {
      --font-size: 1.5rem;

      padding: 1.1rem 1.5rem;
    }
  }

  .xs {
    --font-size: 0.875rem;

    padding: 0.45rem 0.6rem 0.45rem;
  }

  .button.collapse {
    padding: 0;
  }

  .adapt {
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    background: var(--current-background, var(--background));
    color: var(--current-color, rgb(var(--rgb-text)));
    box-shadow: 0 0.125rem 0.25rem rgba(var(--rgb-blue), 0.05);
  }

  .lg.adapt {
    box-shadow: var(--box-shadow-sm);
  }

  .muted {
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    color: rgb(var(--rgb-text));
    background: var(--background-alt);
    font-weight: 500;
  }

  .success {
    background: rgb(var(--rgb-green));
    color: rgb(var(--rgb-white));
  }

  .cover::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  /* Prevent disabled styles from being overridden by turning the switch "off" */
  :global(form:has(:invalid)) .validate,
  .button:is(:disabled, .disabled) {
    --unless-disabled: ;
  }

  .button:is(:disabled, .disabled),
  :global(form:has(:invalid)) .validate {
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    background: var(--background-alt);
    color: rgb(var(--rgb-muted));
    cursor: not-allowed;
    box-shadow: none;
  }

  .button:hover {
    box-shadow: var(--unless-disabled, var(--box-shadow-sm-hover));
  }

  .button:active {
    box-shadow: var(--unless-disabled, var(--box-shadow-sm-active));
    transition: var(
      --unless-disabled,
      var(--box-shadow-active-transition),
      var(--transition)
    );
  }

  .button:not(.adapt, .success) {
    transition: var(
      --unless-disabled,
      var(--background-transition),
      var(--transition)
    );
  }

  .button:not(.adapt, .muted, .success):hover {
    background: var(
      --unless-disabled,
      rgba(var(--rgb-secondary, var(--rgb-dark-red)))
    );
  }

  .muted,
  .muted:not(.adapt, .success):hover,
  .muted:not(.adapt, .success):active {
    background: var(--unless-disabled, var(--background-alt));
  }
</style>
